import React, { useState, useEffect, useMemo, useCallback } from "react";
import axiosInstance from "../../../axiosInstance";
import PayrollHRConfirmation from "./PayrollHRConfirmation";
import { toast } from "react-toastify";
import "./Payroll.scss";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";

function Payroll() {
  // State Management
  const [payrolls, setPayrolls] = useState([]);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [payrollDate, setPayrollDate] = useState("");
  const [calculatedData, setCalculatedData] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [refreshHRConfirmation, setRefreshHRConfirmation] = useState(0);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [onConfirmSubmit, setOnConfirmSubmit] = useState(null);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  // Month data for cards
  const months = [
    { name: "January", number: 1, emoji: "❄️" },
    { name: "February", number: 2, emoji: "💝" },
    { name: "March", number: 3, emoji: "🌸" },
    { name: "April", number: 4, emoji: "🌷" },
    { name: "May", number: 5, emoji: "🌺" },
    { name: "June", number: 6, emoji: "☀️" },
    { name: "July", number: 7, emoji: "🏖️" },
    { name: "August", number: 8, emoji: "🌻" },
    { name: "September", number: 9, emoji: "🍂" },
    { name: "October", number: 10, emoji: "🎃" },
    { name: "November", number: 11, emoji: "🦃" },
    { name: "December", number: 12, emoji: "🎄" },
  ];

  // Auto-generate payroll function
  const autoGeneratePayroll = useCallback(
    async (payroll, month) => {
      const firstDayOfMonth = `${selectedYear}-${month.number
        .toString()
        .padStart(2, "0")}-01`;

      setIsLoading(true);
      setError("");

      try {
        const response = await axiosInstance.post(
          `${process.env.REACT_APP_API_ACCOUNTANT_URL}/comp-And-Ben/calculate-monthly-payroll`,
          {
            payrollId: payroll.payrollId,
            payrollDate: firstDayOfMonth,
          },
        );

        const responseData = response.data.data || [];
        console.log(responseData);

        if (response.status === 201 || responseData.length === 0) {
          setCalculatedData([]);
        } else {
          setCalculatedData(responseData);
          toast.success(
            `Calculated payroll for ${responseData.length} employees`,
            {
              icon: "✅",
            },
          );
        }
      } catch (error) {
        console.error("Error calculating payroll:", error);
        setCalculatedData([]);
        toast.error("Error calculating payroll.");
      } finally {
        setIsLoading(false);
      }
    },
    [selectedYear],
  );

  useEffect(() => {
    const fetchPayrolls = async () => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_ACCOUNTANT_URL}/comp-And-Ben/summaries`,
        );
        const payrollData = response.data.data || [];
        setPayrolls(payrollData);

        // Auto-select first payroll and current month if not already done
        if (payrollData.length > 0 && !hasAutoSelected) {
          const firstPayroll = payrollData[0];
          const currentMonthNumber = new Date().getMonth() + 1;
          const currentMonthData = months.find(
            (month) => month.number === currentMonthNumber,
          );

          // Set the selected payroll
          setSelectedPayroll(firstPayroll);

          if (currentMonthData) {
            setSelectedMonth(currentMonthData);
            const firstDayOfMonth = `${selectedYear}-${currentMonthNumber
              .toString()
              .padStart(2, "0")}-01`;
            setPayrollDate(firstDayOfMonth);

            await autoGeneratePayroll(firstPayroll, currentMonthData);

            // Auto-generate payroll for the selected month (this will handle its own loading state)
            try {
              const payrollResponse = await axiosInstance.post(
                `${process.env.REACT_APP_API_ACCOUNTANT_URL}/comp-And-Ben/calculate-monthly-payroll`,
                {
                  payrollId: firstPayroll.payrollId,
                  payrollDate: firstDayOfMonth,
                },
              );

              const responseData = payrollResponse.data.data || [];

              if (payrollResponse.status === 201 || responseData.length === 0) {
                setCalculatedData([]);
              } else {
                setCalculatedData(responseData);
                toast.success(
                  `Calculated payroll for ${responseData.length} employees`,
                  {
                    icon: "✅",
                  },
                );
              }
            } catch (payrollError) {
              console.error("Error calculating payroll:", payrollError);
              setCalculatedData([]);
              toast.error("Error calculating payroll.");
            }
          }

          setHasAutoSelected(true);
        }
      } catch (error) {
        console.error("Error fetching payrolls:", error);
        toast.error("Failed to load payroll data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayrolls();
  }, [autoGeneratePayroll, hasAutoSelected]); // Remove dependencies to prevent loops

  const tableStructure = useMemo(() => {
    if (calculatedData.length === 0) return null;

    // Essential top-level fields only
    const essentialFields = [
      "employeeName",
      "paidLeave",
      "unPaidLeave",
      "actualGrossSalary",
      "earnedGrossSalary",
      "actualNetSalary",
      "earnedNetSalary",
      "payableAmount",
    ];

    // Extract unique allowance names (EARNINGS - GREEN)
    const allowanceNames = calculatedData.flatMap((employee) =>
      (employee.allowanceDTOs || []).map(
        (allowance) => allowance.allowanceName,
      ),
    );
    const uniqueAllowanceNames = [...new Set(allowanceNames)];

    // Extract unique deduction names (DEDUCTIONS - RED) - Include Income Tax from components
    const deductionNames = calculatedData.flatMap((employee) => {
      const regularDeductions = (employee.deductionDTOs || []).map(
        (deduction) => deduction.deductionName,
      );

      // Check if IT component exists in components array
      const hasITComponent = (employee.components || []).some(
        (component) => component.salaryName === "IT",
      );

      return hasITComponent
        ? [...regularDeductions, "Income Tax"]
        : regularDeductions;
    });
    const uniqueDeductionNames = [...new Set(deductionNames)];

    // Extract unique reimbursement names (REIMBURSEMENTS - BLUE)
    const reimbursementNames = calculatedData.flatMap((employee) =>
      (employee.reimbursementHistoryDtos || employee.reimbursements || []).map(
        (reimbursement) => reimbursement.reimbursementName,
      ),
    );
    const uniqueReimbursementNames = [...new Set(reimbursementNames)];

    // Extract unique emergency fund types (EMERGENCY FUNDS - ORANGE)
    const emergencyFundTypes = calculatedData.flatMap((employee) =>
      (employee.emergencyFund || []).map(
        (fund) => fund.fundType || "Emergency Fund",
      ),
    );
    const uniqueEmergencyFundTypes = [...new Set(emergencyFundTypes)];

    return {
      essentialFields,
      uniqueAllowanceNames,
      uniqueDeductionNames,
      uniqueReimbursementNames,
      uniqueEmergencyFundTypes,
    };
  }, [calculatedData]);

  const getIncomeTaxValue = (employee) => {
    const itComponent = (employee.components || []).find(
      (component) => component.salaryName === "IT",
    );
    return itComponent ? itComponent.amount : 0;
  };

  const updateIncomeTaxInComponents = (employeeIndex, newValue) => {
    setCalculatedData((prevData) => {
      const updatedData = [...prevData];
      const employee = updatedData[employeeIndex];

      if (!employee.components) {
        employee.components = [];
      }

      const itComponentIndex = employee.components.findIndex(
        (component) => component.salaryName === "IT",
      );

      if (itComponentIndex !== -1) {
        // Update existing IT component
        employee.components[itComponentIndex].amount = newValue;
      } else {
        // Create new IT component if it doesn't exist
        employee.components.push({
          paymentSetupId: 9, // Use the same ID as in your data
          salaryName: "IT",
          amount: newValue,
          salaryType: "DEDUCTION",
          contributionType: "EMPLOYEE",
        });
      }

      return updatedData;
    });

    setHasChanges(true);
  };

  // Enhanced payroll selection handler
  const handlePayrollSelection = useCallback((payroll) => {
    setSelectedPayroll(payroll);
    setSelectedMonth(null);
    setPayrollDate("");
    setCalculatedData([]);
    setHasChanges(false);
    setError("");
    // Don't reset hasAutoSelected here to prevent re-triggering auto-selection
  }, []);

  const handleReimbursementEditChange = useCallback((index, name, value) => {
    // Validate numeric input
    if (value !== "" && (isNaN(value) || value < 0)) {
      toast.warn("Please enter a valid positive number.");
      return;
    }

    setCalculatedData((prevData) => {
      const updatedData = [...prevData];
      const targetArray =
        updatedData[index].reimbursementHistoryDtos ||
        updatedData[index].reimbursements ||
        [];

      // Find the correct item by name
      const targetIndex = targetArray.findIndex(
        (item) => item.reimbursementName === name,
      );

      if (targetIndex !== -1) {
        targetArray[targetIndex].amount = value === "" ? "" : parseFloat(value);
      } else {
        // If reimbursement doesn't exist, create it
        targetArray.push({
          reimbursementName: name,
          amount: value === "" ? 0 : parseFloat(value),
          type: "string", // Default type
          reimbursementHistoryId: 0,
        });
      }

      // Update both possible fields
      updatedData[index].reimbursementHistoryDtos = targetArray;
      updatedData[index].reimbursements = targetArray;
      return updatedData;
    });

    setHasChanges(true);
  }, []);

  const handleEmergencyFundEditChange = useCallback(
    (index, fundType, value) => {
      // Validate numeric input
      if (value !== "" && (isNaN(value) || value < 0)) {
        toast.warn("Please enter a valid positive number.");
        return;
      }

      setCalculatedData((prevData) => {
        const updatedData = [...prevData];
        const targetArray = updatedData[index].emergencyFund || [];

        // Find the correct item by fundType
        const targetIndex = targetArray.findIndex(
          (item) => (item.fundType || "Emergency Fund") === fundType,
        );

        if (targetIndex !== -1) {
          targetArray[targetIndex].amount =
            value === "" ? "" : parseFloat(value);
        } else {
          // If emergency fund doesn't exist, create it
          targetArray.push({
            fundId: 0,
            fundType: fundType,
            amount: value === "" ? 0 : parseFloat(value),
            paymentDate: payrollDate,
          });
        }

        updatedData[index].emergencyFund = targetArray;
        return updatedData;
      });

      setHasChanges(true);
    },
    [payrollDate],
  );

  // Recalculate payroll function
  const handleRecalculatePayroll = useCallback(async () => {
    if (!calculatedData || calculatedData.length === 0) {
      toast.error("No payroll data to recalculate.");
      return;
    }

    if (!payrollDate) {
      toast.error("Please select a month first.");
      return;
    }

    setIsRecalculating(true);
    setError("");

    try {
      // Process each employee individually for recalculation
      const recalculationPromises = calculatedData.map(async (employee) => {
        // Ensure LOP value is properly extracted - use unPaidLeave as the source of truth
        const lopValue = employee.unPaidLeave || employee.lossOfPayDays || 0;

        console.log(
          `Employee ${employee.employeeName}: LOP = ${lopValue}, unPaidLeave = ${employee.unPaidLeave}, lossOfPayDays = ${employee.lossOfPayDays}`,
        );

        const payload = {
          employeeId: employee.employeeId || 0,
          payrollDate: payrollDate,
          lossOfPayDays: lopValue, // Use the determined LOP value
          incomeTaxAmount: getIncomeTaxValue(employee), // Use helper function for IT
          earnedOtherAllowances: employee.earnedOtherAllowances || 0,
          earnedOtherDeductions: employee.earnedOtherDeductions || 0,
          allowanceDTOs: (employee.allowanceDTOs || []).map((allowance) => ({
            allowanceId: allowance.allowanceId || 0,
            allowanceName: allowance.allowanceName || "",
            amount: allowance.amount || 0,
          })),
          deductionDTOs: (employee.deductionDTOs || []).map((deduction) => ({
            deductionId: deduction.deductionId || 0,
            deductionName: deduction.deductionName || "",
            amount: deduction.amount || 0,
          })),
        };

        console.log(`Payload for ${employee.employeeName}:`, payload);

        console.log(JSON.stringify(payload, null, 2));

        return axiosInstance.post(
          `${process.env.REACT_APP_API_ACCOUNTANT_URL}/comp-And-Ben/recalculate-monthly-payroll`,
          payload,
        );
      });

      // Wait for all recalculation requests to complete
      const responses = await Promise.all(recalculationPromises);

      console.log("Recalculation responses:", responses);

      // Combine all response data
      const combinedResponseData = responses.flatMap(
        (response) => response.data.data || [],
      );

      if (combinedResponseData.length > 0) {
        // Update the calculated data with the recalculated values
        // Preserve the current LOP values from the UI
        const updatedData = combinedResponseData.map((newEmployee, index) => {
          const currentEmployee = calculatedData[index];
          return {
            ...newEmployee,
            // Preserve the LOP values that were set in the UI
            unPaidLeave: currentEmployee.unPaidLeave,
            lossOfPayDays:
              currentEmployee.unPaidLeave || currentEmployee.lossOfPayDays || 0,
          };
        });

        setCalculatedData(updatedData);
        setHasChanges(false);

        toast.success("Payroll recalculated successfully! 🔄", {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        toast.info("No data returned from recalculation.", {
          icon: "ℹ️",
        });
      }
    } catch (error) {
      console.error("Error recalculating payroll:", error);
      const errorMessage =
        error.response?.data?.message || "Error recalculating payroll.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsRecalculating(false);
    }
  }, [calculatedData, payrollDate, getIncomeTaxValue]);

  // Updated Month selection handler with auto-generation
  const handleMonthSelection = useCallback(
    async (month) => {
      if (!selectedPayroll) {
        toast.error("Please select a payroll configuration first.");
        return;
      }

      const firstDayOfMonth = `${selectedYear}-${month.number
        .toString()
        .padStart(2, "0")}-01`;

      setSelectedMonth(month);
      setPayrollDate(firstDayOfMonth);
      setCalculatedData([]);
      setHasChanges(false);
      setError("");

      // Auto-generate payroll
      await autoGeneratePayroll(selectedPayroll, month);
    },
    [selectedPayroll, selectedYear, autoGeneratePayroll],
  );

  // Enhanced edit change handler with validation
  const handleEditChange = useCallback((index, key, { name, value }) => {
    // Validate numeric input
    if (value !== "" && (isNaN(value) || value < 0)) {
      toast.warn("Please enter a valid positive number.");
      return;
    }

    setCalculatedData((prevData) => {
      const updatedData = [...prevData];
      const targetArray = updatedData[index][key];

      // Find the correct item by name
      const targetIndex = targetArray.findIndex(
        (item) => item.allowanceName === name || item.deductionName === name,
      );

      if (targetIndex !== -1) {
        targetArray[targetIndex].amount = value === "" ? "" : parseFloat(value);
      }
      return updatedData;
    });

    setHasChanges(true);
  }, []);

  const handleSubmit = useCallback(
    async (skipConfirmation = false) => {
      // Show warning popup if no changes but data exists (unless skipping confirmation)
      if (!skipConfirmation && !hasChanges && calculatedData.length > 0) {
        setOnConfirmSubmit(() => () => handleSubmit(true));
        setShowWarningPopup(true);
        return;
      }

      const payload = calculatedData.map((employee) => ({
        employeeId: employee.employeeId || 0,
        paidLeave: employee.paidLeave || 0,
        unPaidLeave: employee.unPaidLeave || 0,
        earnedGrossSalary: employee.earnedGrossSalary || 0,
        earnedNetSalary: employee.earnedNetSalary || 0,
        // Fixed mapping: Map from response keys to request keys
        earnedOtherAllowances: employee.totalOtherAllowances || 0,
        earnedOtherDeductions: employee.totalOtherDeductions || 0,
        earnedCTC: employee.earnedCTC || 0,
        actualGrossSalary: employee.actualGrossSalary || 0,
        actualNetSalary: employee.actualNetSalary || 0,
        actualCTC: employee.actualCTC || 0,
        payableAmount: employee.payableAmount || 0,
        paidAmount: employee.paidAmount || 0,
        employeeDeductions: employee.employeeDeductions || 0,
        employerDeductions: employee.employerDeductions || 0,
        totalReimbursementAmount: employee.totalReimbursementAmount || 0,
        totalLoanInstallmentAmount: employee.totalLoanInstallmentAmount || 0,
        payrollDate: payrollDate,
        paymentMode: "NEFT", // Default payment mode as per API schema

        // FIXED: Map emergencyFund to totalLoanInstallments
        loanInstallments: Array.isArray(employee.emergencyFund)
          ? employee.emergencyFund.map((fund) => ({
              fundId: fund.fundId || 0,
              paymentDate: fund.paymentDate || payrollDate,
              amount: fund.amount || 0,
              fundType: fund.fundType || "LOAN",
            }))
          : [],

        components: Array.isArray(employee.components)
          ? employee.components.map((component) => ({
              paymentSetupId: component.paymentSetupId || 0,
              salaryName: component.salaryName || "string",
              amount: component.amount || 0,
              salaryType: component.salaryType || "EARNING",
              contributionType: component.contributionType || "EMPLOYEE",
              taxType: component.taxType || "IT",
            }))
          : [],

        allowanceDTOs: Array.isArray(employee.allowanceDTOs)
          ? employee.allowanceDTOs.map((allowance) => ({
              allowanceId: allowance.allowanceId || 0,
              allowanceName: allowance.allowanceName || "string",
              amount: allowance.amount || 0,
            }))
          : [],

        deductionDTOs: Array.isArray(employee.deductionDTOs)
          ? employee.deductionDTOs.map((deduction) => ({
              deductionId: deduction.deductionId || 0,
              deductionName: deduction.deductionName || "string",
              amount: deduction.amount || 0,
            }))
          : [],

        reimbursementHistoryDtos: Array.isArray(
          employee.reimbursements || employee.reimbursementHistoryDtos,
        )
          ? (employee.reimbursements || employee.reimbursementHistoryDtos).map(
              (reimbursement) => ({
                type: reimbursement.type || "string",
                reimbursementName: reimbursement.reimbursementName || "string",
                amount: reimbursement.amount || 0,
                reimbursementHistoryId:
                  reimbursement.reimbursementHistoryId || 0,
              }),
            )
          : [],
      }));

      if (payload.length === 0) {
        toast.error("No data to submit.");
        return;
      }

      setIsSubmitting(true);

      console.log(payload);

      try {
        const response = await axiosInstance.post(
          `${process.env.REACT_APP_API_ACCOUNTANT_URL}/comp-And-Ben/generate`,
          payload,
        );

        toast.success("Payroll submitted successfully! 🎉", {
          position: "top-center",
          autoClose: 3000,
        });

        setHasChanges(false);
        setRefreshHRConfirmation((prev) => prev + 1);

        if (selectedPayroll && selectedMonth) {
          await autoGeneratePayroll(selectedPayroll, selectedMonth);
        }

        if (selectedPayroll && selectedMonth) {
          // First re-select the payroll (this will clear the month)
          setSelectedPayroll(selectedPayroll);

          // Then re-select the month with auto-generation
          await handleMonthSelection(selectedMonth);
        } else if (selectedPayroll) {
          handlePayrollSelection(selectedPayroll);
        }
      } catch (error) {
        console.error("Error submitting payroll:", error);
        const errorMessage =
          error.response?.data?.message ||
          "There was an error submitting the payroll.";
        toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      calculatedData,
      hasChanges,
      payrollDate,
      selectedPayroll,
      handlePayrollSelection,
      getIncomeTaxValue,
      autoGeneratePayroll,
    ],
  );

  const renderTableHeaders = useCallback(() => {
    if (!tableStructure) return null;

    const {
      essentialFields,
      uniqueAllowanceNames,
      uniqueDeductionNames,
      uniqueReimbursementNames,
      uniqueEmergencyFundTypes,
    } = tableStructure;

    // Helper function to format field names
    const formatFieldName = (key) => {
      const fieldLabels = {
        employeeName: "Employee",
        paidLeave: "Paid Leave",
        unPaidLeave: "Unpaid Leave",
        actualGrossSalary: "Actual Gross",
        earnedGrossSalary: "Earned Gross",
        actualNetSalary: "Actual Net",
        earnedNetSalary: "Earned Net",
        payableAmount: "Payable Amount",
        incomeTaxAmount: "Income Tax",
      };
      return (
        fieldLabels[key] ||
        key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
      );
    };

    return (
      <>
        {/* First Row - Group Headers */}
        <tr className="group-header-row">
          {/* Essential Info Columns */}
          {essentialFields.map((key, index) => (
            <th
              key={`group-${key}`}
              className={
                key === "employeeName"
                  ? "fixed-column employee-column group-header"
                  : key === "incomeTaxAmount"
                    ? "deduction-header group-header" // Special styling for income tax
                    : "basic-info group-header"
              }
              rowSpan="2"
            >
              {formatFieldName(key)}
            </th>
          ))}
          {/* Allowances Group Header */}
          {uniqueAllowanceNames.length > 0 && (
            <th
              className="allowance-header group-header allowance-group"
              colSpan={uniqueAllowanceNames.length}
            >
              💚 Allowances
              <span className="status-indicator">
                {uniqueAllowanceNames.length}
              </span>
            </th>
          )}

          {/* Reimbursements Group Header */}
          {uniqueReimbursementNames.length > 0 && (
            <th
              className="reimbursement-header group-header reimbursement-group"
              colSpan={uniqueReimbursementNames.length}
            >
              💙 Reimbursements
              <span className="status-indicator">
                {uniqueReimbursementNames.length}
              </span>
            </th>
          )}

          {/* Emergency Funds Group Header */}
          {uniqueEmergencyFundTypes.length > 0 && (
            <th
              className="emergency-fund-header group-header emergency-fund-group"
              colSpan={uniqueEmergencyFundTypes.length}
            >
              🧡 Emergency Funds
              <span className="status-indicator">
                {uniqueEmergencyFundTypes.length}
              </span>
            </th>
          )}

          {/* Deductions Group Header */}
          {uniqueDeductionNames.length > 0 && (
            <th
              className="deduction-header group-header deduction-group"
              colSpan={uniqueDeductionNames.length}
            >
              ❤️ Deductions
              <span className="status-indicator">
                {uniqueDeductionNames.length}
              </span>
            </th>
          )}
        </tr>

        {/* Second Row - Individual Column Headers */}
        <tr className="individual-header-row">
          {/* Allowances Individual Headers */}
          {uniqueAllowanceNames.map((name) => (
            <th
              key={`allowance-${name}`}
              className="allowance-header sub-header"
            >
              {name}
            </th>
          ))}

          {/* Reimbursements Individual Headers */}
          {uniqueReimbursementNames.map((name) => (
            <th
              key={`reimbursement-${name}`}
              className="reimbursement-header sub-header"
            >
              {name}
            </th>
          ))}

          {/* Emergency Funds Individual Headers */}
          {uniqueEmergencyFundTypes.map((fundType) => (
            <th
              key={`emergency-fund-${fundType}`}
              className="emergency-fund-header sub-header"
            >
              {fundType}
            </th>
          ))}

          {/* Deductions Individual Headers */}
          {uniqueDeductionNames.map((name) => (
            <th
              key={`deduction-${name}`}
              className="deduction-header sub-header"
            >
              {name}
            </th>
          ))}
        </tr>
      </>
    );
  }, [tableStructure]);

  // Enhanced table rows renderer (unchanged functionality)
  const renderTableRows = useCallback(() => {
    if (!calculatedData || calculatedData.length === 0 || !tableStructure)
      return null;

    const {
      essentialFields,
      uniqueAllowanceNames,
      uniqueDeductionNames,
      uniqueReimbursementNames,
      uniqueEmergencyFundTypes,
    } = tableStructure;

    return calculatedData.map((employee, index) => (
      <tr key={`employee-${employee.employeeId || index}`}>
        {essentialFields.map((key, keyIndex) => {
          if (key === "unPaidLeave") {
            return (
              <td key={key} className="basic-info">
                <input
                  type="number"
                  className="editable-field"
                  value={
                    employee.unPaidLeave === "" ||
                    employee.unPaidLeave == null ||
                    employee.unPaidLeave === 0
                      ? ""
                      : employee.unPaidLeave
                  }
                  placeholder="0"
                  onChange={(e) => {
                    const updatedData = [...calculatedData];
                    const newValue =
                      e.target.value === ""
                        ? 0
                        : parseFloat(e.target.value) || 0;

                    // Update both unPaidLeave and lossOfPayDays consistently
                    updatedData[index].unPaidLeave = newValue;
                    updatedData[index].lossOfPayDays = newValue;

                    console.log(
                      `Updated LOP for ${employee.employeeName}: ${newValue}`,
                    );

                    setCalculatedData(updatedData);
                    setHasChanges(true);
                  }}
                  min="0"
                  step="1"
                  title="Edit Unpaid Leave days"
                />
              </td>
            );
          }

          // Rest of the existing logic remains the same
          const isDisabled =
            key === "employeeName" ||
            key === "paidLeave" ||
            key === "actualGrossSalary" ||
            key === "earnedGrossSalary" ||
            key === "actualNetSalary" ||
            key === "earnedNetSalary" ||
            key === "payableAmount";

          const isCurrencyField = key !== "employeeName" && key !== "paidLeave";

          return (
            <td
              key={key}
              className={
                keyIndex === 0 ? "fixed-column employee-name" : "basic-info"
              }
            >
              <span className="readonly-value">
                {isCurrencyField
                  ? `₹${(employee[key] || 0).toFixed(2)}`
                  : employee[key] || "0"}
              </span>
            </td>
          );
        })}

        {/* Allowances */}
        {uniqueAllowanceNames.map((name) => {
          const allowance = (employee.allowanceDTOs || []).find(
            (allow) => allow.allowanceName === name,
          );
          const amount = allowance?.amount;
          return (
            <td key={`allowance-${name}-${index}`} className="allowance-cell">
              <input
                type="number"
                value={
                  amount === 0 || amount == null || amount === "" ? "" : amount
                }
                placeholder="0.00"
                onChange={(e) =>
                  handleEditChange(index, "allowanceDTOs", {
                    name: name,
                    value: e.target.value,
                  })
                }
                min="0"
                step="0.01"
                title={`Edit ${name} allowance amount`}
              />
            </td>
          );
        })}

        {/* Reimbursements */}
        {uniqueReimbursementNames.map((name) => {
          const reimbursement = (
            employee.reimbursementHistoryDtos ||
            employee.reimbursements ||
            []
          ).find((reimb) => reimb.reimbursementName === name);
          const amount = reimbursement?.amount;
          return (
            <td
              key={`reimbursement-${name}-${index}`}
              className="reimbursement-cell"
            >
              <input
                type="number"
                value={
                  amount === 0 || amount == null || amount === "" ? "" : amount
                }
                placeholder="0.00"
                onChange={(e) =>
                  handleReimbursementEditChange(index, name, e.target.value)
                }
                min="0"
                step="0.01"
                title={`Edit ${name} reimbursement amount`}
              />
            </td>
          );
        })}

        {/* Emergency Funds */}
        {uniqueEmergencyFundTypes.map((fundType) => {
          const fund = (employee.emergencyFund || []).find(
            (f) => (f.fundType || "Emergency Fund") === fundType,
          );
          const amount = fund?.amount;
          return (
            <td
              key={`emergency-fund-${fundType}-${index}`}
              className="emergency-fund-cell"
            >
              <input
                type="number"
                value={
                  amount === 0 || amount == null || amount === "" ? "" : amount
                }
                placeholder="0.00"
                onChange={(e) =>
                  handleEmergencyFundEditChange(index, fundType, e.target.value)
                }
                min="0"
                step="0.01"
                title={`Edit ${fundType} amount`}
              />
            </td>
          );
        })}

        {/* Deductions */}
        {uniqueDeductionNames.map((name) => {
          let deduction;
          let value;

          if (name === "Income Tax") {
            value = getIncomeTaxValue(employee);
          } else {
            deduction = (employee.deductionDTOs || []).find(
              (deduct) => deduct.deductionName === name,
            );
            value = deduction?.amount;
          }

          return (
            <td key={`deduction-${name}-${index}`} className="deduction-cell">
              <input
                type="number"
                value={
                  value === null || value === undefined || value === 0
                    ? ""
                    : value
                }
                placeholder="0.00"
                onChange={(e) => {
                  if (name === "Income Tax") {
                    const newValue =
                      e.target.value === ""
                        ? 0
                        : parseFloat(e.target.value) || 0;
                    updateIncomeTaxInComponents(index, newValue);
                  } else {
                    handleEditChange(index, "deductionDTOs", {
                      name: name,
                      value: e.target.value,
                    });
                  }
                }}
                min="0"
                step="0.01"
                title={`Edit ${name} ${
                  name === "Income Tax" ? "" : "deduction "
                }amount`}
              />
            </td>
          );
        })}
      </tr>
    ));
  }, [
    calculatedData,
    tableStructure,
    handleEditChange,
    handleReimbursementEditChange,
    handleEmergencyFundEditChange,
    getIncomeTaxValue,
    updateIncomeTaxInComponents,
  ]);

  return (
    <div className="payroll-container">
      {/* Enhanced Payroll Selection */}
      <div className="payroll-selection">
        <div className="payroll-selection-header">
          <h2 className="heading6">Select Payroll Configuration</h2>
          <div className="year-selector">
            <div className="year-arrow-controls">
              <button
                className="year-arrow left"
                aria-label="Previous year"
                type="button"
                disabled={selectedYear <= 1900}
                onClick={(e) => {
                  e.stopPropagation?.();
                  setSelectedYear((y) => Math.max(1900, y - 1));
                }}
              >
                {/* SVG Left Arrow */}
                <svg viewBox="0 0 24 24" className="icon">
                  <path d="M15.7 5.7a1 1 0 0 1 0 1.4L10.42 12l5.3 4.9a1 1 0 0 1-1.42 1.4l-6-5.5a1 1 0 0 1 0-1.4l6-5.5a1 1 0 0 1 1.4 0z" />
                </svg>
              </button>
              <input
                id="year-input"
                type="number"
                min="1900"
                max="2100"
                value={selectedYear}
                onChange={(e) => {
                  let year = parseInt(e.target.value, 10);
                  if (!isNaN(year) && year > 0 && year <= 9999)
                    setSelectedYear(year);
                }}
                className="year-value"
                placeholder="YYYY"
                onClick={(e) => e.stopPropagation?.()}
              />
              <button
                className="year-arrow right"
                aria-label="Next year"
                type="button"
                disabled={selectedYear >= 2100}
                onClick={(e) => {
                  e.stopPropagation?.();
                  setSelectedYear((y) => Math.min(2100, y + 1));
                }}
              >
                {/* SVG Right Arrow */}
                <svg viewBox="0 0 24 24" className="icon">
                  <path d="M8.3 18.3a1 1 0 0 1 0-1.4L13.58 12 8.3 7.1a1 1 0 0 1 1.42-1.4l6 5.5a1 1 0 0 1 0 1.4l-6 5.5a1 1 0 0 1-1.4 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="payroll-buttons-grid">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="loading-skeleton"
                style={{ height: "64px" }}
              ></div>
            ))}
          </div>
        ) : (
          <div className="payroll-buttons-grid">
            {payrolls.length > 0 ? (
              payrolls.map((payroll) => (
                <button
                  key={payroll.payrollId}
                  onClick={() => handlePayrollSelection(payroll)}
                  className={`payroll-button ${
                    selectedPayroll?.payrollId === payroll.payrollId
                      ? "active"
                      : ""
                  }`}
                  disabled={isLoading || isSubmitting}
                >
                  <div className="payroll-button-content">
                    <div className="payroll-text">
                      <div className="payroll-name">
                        {payroll.payrollName || `Payroll ${payroll.payrollId}`}
                      </div>
                      <div
                        className={`payroll-type-badge ${
                          payroll.payrollSetupType?.toLowerCase() === "auto"
                            ? "auto"
                            : "manual"
                        }`}
                      >
                        <span className="badge-icon">⚙️</span>
                        {payroll.payrollSetupType}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="error">
                No payroll configurations found. Please create a payroll
                configuration first.
              </div>
            )}
          </div>
        )}

        {/* Month Selection Section */}
        {selectedPayroll && (
          <div className="month-selection">
            <div className="month-cards-grid">
              {months.map((month) => (
                <button
                  key={month.number}
                  onClick={() => handleMonthSelection(month)}
                  className={`month-card ${
                    selectedMonth?.number === month.number ? "active" : ""
                  }`}
                  disabled={isLoading || isSubmitting}
                >
                  <div className="month-emoji">{month.emoji}</div>
                  <div className="month-name">{month.name}</div>
                  <div className="month-year">{selectedYear}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Enhanced Payroll Results */}
      {calculatedData.length > 0 && (
        <div
          className="payroll-results"
          style={{
            borderRadius: "20px",
          }}
        >
          <div
            style={{
              padding: "20px 24px",
              borderBottom: "1px solid #e2e8f0",
              background: "#f8fafc",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              📊 Payroll Results
              <span className="status-indicator success">
                {calculatedData.length} Employee
                {calculatedData.length !== 1 ? "s" : ""}
              </span>
              {hasChanges && (
                <span className="status-indicator warning">Modified</span>
              )}
            </h3>
            <p
              style={{
                margin: "8px 0 0 0",
                fontSize: "0.9rem",
                color: "#64748b",
              }}
            >
              💚 <strong>Green:</strong> Allowances | 💙 <strong>Blue:</strong>{" "}
              Reimbursements | 🧡 <strong>Orange:</strong> Emergency Funds | ❤️{" "}
              <strong>Red:</strong> Deductions
              <br />
              <span style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                Edit values directly in colored columns. Essential info columns
                are read-only except Leave fields.
              </span>
            </p>
          </div>

          <div
            style={{ overflowX: "auto", maxHeight: "600px", overflowY: "auto" }}
          >
            <div className="payroll-table-wrapper">
              <table>
                <thead>{renderTableHeaders()}</thead>
                <tbody>{renderTableRows()}</tbody>
              </table>
            </div>
          </div>

          {/* Summary Footer */}
          {/* Summary Footer */}
          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #e2e8f0",
              background: "#f8fafc",
              fontSize: "0.9rem",
              color: "#64748b",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                💡 <strong>Tip:</strong> Edit allowances, reimbursements,
                emergency funds, and deductions directly in the table
              </span>
              {hasChanges && (
                <span style={{ color: "#ea580c", fontWeight: 600 }}>
                  Remember to submit your changes
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {calculatedData.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginTop: "20px",
            flexWrap: "wrap",
          }}
        >
          <ButtonWithIcon
            text={isRecalculating ? "Recalculating..." : "Recalculate"}
            icon={isRecalculating ? "mdi:loading mdi-spin" : "mdi:calculator"}
            onClick={handleRecalculatePayroll}
            disabled={isLoading || isSubmitting || isRecalculating}
            className={isRecalculating ? "info" : "warning"}
          />

          <ButtonWithIcon
            text={isSubmitting ? "Processing..." : "Process Payroll"}
            icon={isSubmitting ? "mdi:loading mdi-spin" : "mdi:check-circle"}
            onClick={handleSubmit}
            disabled={
              isLoading || isSubmitting || isRecalculating || hasChanges
            }
            className={isSubmitting ? "info" : "success"}
          />
        </div>
      )}

      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={() => {
          setShowWarningPopup(false);
          onConfirmSubmit?.();
        }}
        onDecline={() => setShowWarningPopup(false)}
        title="No Changes Detected"
        message="No changes were made. Do you still want to submit the payroll?"
        acceptText="Yes, Submit"
        declineText="Cancel"
      />

      {selectedPayroll && (
        <div style={{ marginTop: "24px" }}>
          <h1 className="display6">Payroll Sheet</h1>
          <PayrollHRConfirmation
            payrollId={selectedPayroll.payrollId}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            key={refreshHRConfirmation}
          />
        </div>
      )}
    </div>
  );
}

export default Payroll;
